import {
  SMART_LIST_GROUPS,
  dialerQueueSchema,
  isEmptyFilter,
  smartListDetailSchema,
  smartListRevisionsResponseSchema,
  smartListsResponseSchema,
  smartListWriteResponseSchema,
  type CreateSmartListBody,
  type FilterDefinition,
  type LeadListQuery,
  type RenameSmartListBody,
  type SaveSmartListBody,
  type SaveSmartListOrderBody,
  type ShareSmartListBody,
  type SmartListDetail,
  type SmartListSort,
  type SmartListsResponse,
  type SmartListSummary,
  type TableScope,
} from "@gioia365/contracts";
import { queryOptions, skipToken, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { normalizeSearchText } from "@gioia365/domain/search-text";
import { z } from "zod";
import { apiRequest } from "../api/http";
import { savedDialerSessionQuery } from "../dialer/dialer-queries";
import type { Translate } from "../i18n/locale";
import { tableLayoutQuery } from "../leads/columns/layout-queries";
import { companyFieldKeys, deviceStorage, listIncludes, readDeviceLayout, resolveTableView, tableScopeKey } from "../leads/columns/table-layout";
import { leadCatalogQuery } from "../leads/filters/filter-queries";
import { LEAD_PAGE_SIZE, leadKeys, leadListQuery } from "../leads/lead-queries";
import { commercialViewOf, tabKey, type TabContext, type TabKey } from "../session/tab-context";
import { smartListMenuKey, smartListMenuPrefix } from "./menu-refresh";

// Le letture e le scritture delle Smart List (piano M3 Task 8, rotte del Task 7). Le chiavi stanno sotto
// `["scheda", tabKey, …]`, così il cambio di azienda le toglie tutte insieme. Il menu porta la Vista Commerciale, perché
// i numeri accanto alle liste contano i Lead del perimetro di chi guarda (con la vista, quelli del Commerciale osservato);
// una lista, la sua definizione e il suo storico non cambiano con la vista. Ogni scrittura riuscita mette la lista nuova
// nella cache della lista e rilegge il menu (nomi, gruppi, numeri).

export const smartListKeys = {
  /** Il menu, con i conteggi del perimetro della vista (`menu-refresh.ts`, che lo rilegge dopo le scritture sui Lead). */
  all: smartListMenuKey,
  one: (tab: TabKey, id: string) => ["scheda", tab, "smart-list", id] as const,
  revisions: (tab: TabKey, id: string) => ["scheda", tab, "smart-list", id, "revisions"] as const,
  /** La sessione del Power Dialer interrotta su questa lista, di chi guarda (piano M5, Task 6). */
  savedSession: (tab: TabKey, id: string) => ["scheda", tab, "smart-list", id, "dialer-saved"] as const,
  /** I Lead già fatti in quella sessione, con il loro segno. */
  savedMarks: (tab: TabKey, id: string, sessionId: string, interruptedAt: string) =>
    ["scheda", tab, "smart-list", id, "dialer-saved", sessionId, interruptedAt, "worked"] as const,
};

/** Il prefisso del menu per ogni vista: una scrittura lo rilegge tutto. */
const menuPrefix = (tab: TabContext) => smartListMenuPrefix(tabKey(tab));

/** Nello `Storage` della scheda (il `sessionStorage`): l'ultima lista aperta, che la voce «Smart List» riapre. */
export const LAST_SMART_LIST_KEY = "gioia365.smart-list.ultima";

/**
 * Il menu delle Smart List (`GET /api/smart-lists`): le liste che chi guarda vede, per gruppo e per nome, ognuna con il
 * numero dei suoi Lead. Il server conta fino a cento liste (decisione P26): il menu resta fresco per un minuto, così il
 * ritorno sulla finestra o il passaggio sulla voce non rifanno tutti i conteggi; una scrittura lo rilegge da sé.
 */
export function smartListsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: smartListKeys.all(tabKey(tab), commercialViewOf(tab)),
    queryFn: ({ signal }) => apiRequest("GET", "/api/smart-lists", { schema: smartListsResponseSchema, tab, signal }),
    staleTime: 60_000,
  });
}

/**
 * Legge in anticipo il menu delle liste e, appena arriva, la lista che la voce «Smart List» aprirà (l'ultima aperta nella
 * scheda, altrimenti la prima: `pickSmartList`), così il passaggio alla sezione la mostra subito (velocità: criterio di
 * accettazione, decisione P28). La chiama la shell come per i Lead, con lo `Storage` della scheda. Non rilegge un menu
 * né una lista ancora freschi.
 */
export async function prefetchSmartLists(queryClient: QueryClient, tab: TabContext, storage: Storage): Promise<void> {
  const options = smartListsQuery(tab);
  await queryClient.prefetchQuery(options);
  const menu = queryClient.getQueryData(options.queryKey);
  const target = menu ? pickSmartList(menu.lists, rememberedSmartList(storage)) : null;
  if (target) await prefetchSmartList(queryClient, tab, target.id);
}

/** Il contesto delle colonne di una lista: la sua vista (piano M3 Task 6 e 7, D5). */
export function smartListTableScope(id: string): TableScope {
  return { scopeType: "smart_list", scopeId: id };
}

/**
 * La lettura dei Lead di una lista, senza la pagina (`GET /api/leads`): la definizione (salvata o in bozza), l'ordine di
 * chiamata, i campi dell'azienda e le colonne di sistema da leggere delle colonne visibili (`fields`, `include`) e la ricerca
 * nei risultati; sempre `who = all`, perché una
 * lista non salva «Di chi» (decisione P5): le righe sono la definizione nel perimetro di chi guarda, lo stesso numero
 * del menu. La stessa forma serve alla pagina e alla lettura in anticipo, così la chiave è la stessa.
 */
export function smartListLeadsQuery(input: { definition: FilterDefinition; sort: SmartListSort; fields: string; include: string; q: string }): Omit<LeadListQuery, "offset"> {
  return {
    ...(input.q ? { q: input.q } : {}),
    who: "all",
    ...(isEmptyFilter(input.definition) ? {} : { filter: input.definition }),
    ...(input.fields ? { fields: input.fields } : {}),
    ...(input.include ? { include: input.include } : {}),
    sort: input.sort.field,
    direction: input.sort.direction,
    limit: LEAD_PAGE_SIZE,
  };
}

/** Le parole non servono per sapere quali colonne sono visibili: soltanto gli identificativi. */
const keysOnly: Translate = (key) => key;

/**
 * I campi dell'azienda e le colonne di sistema da leggere visibili nella vista che la pagina della lista userà: la stessa regola
 * di `useTableLayout` e di `useColumnArrange`.
 */
function visibleColumnReads(queryClient: QueryClient, tab: TabContext, scope: TableScope): { fields: string; include: string } {
  const server = queryClient.getQueryData(tableLayoutQuery(tab, scope).queryKey);
  const catalog = queryClient.getQueryData(leadCatalogQuery(tab).queryKey);
  const device = readDeviceLayout(deviceStorage(), tabKey(tab), tableScopeKey(scope));
  const view = resolveTableView({ t: keysOnly, catalog, company: server?.company?.columns ?? null, personal: server?.personal?.columns ?? null, device });
  return { fields: companyFieldKeys(view.resolved.columns).join(","), include: listIncludes(view.resolved.columns) };
}

/**
 * Legge in anticipo una lista che sta per aprirsi (il puntatore o il focus sul suo nome nel sottomenu, la voce «Smart
 * List»): la lista, la sua vista delle colonne, i campi dell'azienda e poi la prima pagina dei suoi Lead, con la stessa
 * chiave della pagina, così la lista si apre già con le righe (velocità, decisione P28). Non rilegge ciò che è fresco.
 */
export async function prefetchSmartList(queryClient: QueryClient, tab: TabContext, id: string): Promise<void> {
  const scope = smartListTableScope(id);
  await Promise.all([
    queryClient.prefetchQuery(smartListQuery(tab, id)),
    queryClient.prefetchQuery(tableLayoutQuery(tab, scope)),
    queryClient.prefetchQuery(leadCatalogQuery(tab)),
  ]);
  const list = queryClient.getQueryData(smartListQuery(tab, id).queryKey);
  if (!list) return;
  const query = smartListLeadsQuery({ definition: list.definition, sort: list.sort, ...visibleColumnReads(queryClient, tab, scope), q: "" });
  await queryClient.prefetchQuery(leadListQuery(tab, { ...query, offset: 0 }, commercialViewOf(tab)));
}

function listPath(id: string): string {
  return `/api/smart-lists/${encodeURIComponent(id)}`;
}

/** Una lista (`GET /api/smart-lists/:id`): nome, definizione pulita, ordinamento, persone e permessi di chi guarda. */
export function smartListQuery(tab: TabContext, id: string) {
  return queryOptions({
    queryKey: smartListKeys.one(tabKey(tab), id),
    queryFn: ({ signal }) => apiRequest("GET", listPath(id), { schema: smartListDetailSchema, tab, signal }),
    // Appena creata, salvata o aperta dal menu non si rilegge subito.
    staleTime: 30_000,
  });
}

/** «Storico della lista» (`GET /api/smart-lists/:id/revisions`): le ultime cento versioni, dalla più recente. */
export function smartListRevisionsQuery(tab: TabContext, id: string) {
  return queryOptions({
    queryKey: smartListKeys.revisions(tabKey(tab), id),
    queryFn: async ({ signal }) => (await apiRequest("GET", `${listPath(id)}/revisions`, { schema: smartListRevisionsResponseSchema, tab, signal })).revisions,
  });
}

/**
 * La lista che la voce «Smart List» apre: l'ultima aperta nella scheda, se è ancora fra quelle del menu, altrimenti la
 * prima; `null` senza liste.
 */
export function pickSmartList(lists: readonly SmartListSummary[], remembered: string | null): SmartListSummary | null {
  return lists.find((list) => list.id === remembered) ?? lists[0] ?? null;
}

/** L'ultima lista aperta nella scheda, oppure `null` (anche con lo `Storage` che non risponde). */
export function rememberedSmartList(storage: Storage): string | null {
  try {
    return storage.getItem(LAST_SMART_LIST_KEY);
  } catch {
    return null;
  }
}

/** Ricorda l'ultima lista aperta nella scheda; senza `Storage` non ricorda nulla. */
export function rememberSmartList(storage: Storage, id: string): void {
  try {
    storage.setItem(LAST_SMART_LIST_KEY, id);
  } catch {
    // Archiviazione negata dal browser: la voce riaprirà la prima lista.
  }
}

/**
 * La pagina della lista si è aperta (`POST /api/smart-lists/:id/opened`, riscontro del product owner del 3 ottobre 2026,
 * questione aperta 182): il server azzera le «novità» della lista per chi guarda, e il menu in cache le toglie subito,
 * senza rifare i conteggi. Soltanto per chi apre la lista per sé, anche il Super admin di Gioia365 nel contesto di
 * piattaforma, che ha le sue novità per la sua identità (la richiesta lascia la sua riga nel registro dell'azienda, D13);
 * non nella Vista Commerciale, dove le novità sono della persona osservata e restano sue. Un rifiuto o un guasto non si
 * mostrano: il pallino resta fino alla prossima apertura. Se il menu si sta rileggendo proprio adesso, la sua risposta
 * potrebbe essere di prima dell'apertura: si rilegge di nuovo.
 */
export async function recordSmartListOpening(queryClient: QueryClient, tab: TabContext, id: string): Promise<void> {
  if (commercialViewOf(tab) !== null) return;
  try {
    await apiRequest("POST", `${listPath(id)}/opened`, { schema: z.undefined(), tab });
  } catch {
    return;
  }
  const queryKey = smartListKeys.all(tabKey(tab), null);
  const reading = queryClient.isFetching({ queryKey, exact: true }) > 0;
  queryClient.setQueryData<SmartListsResponse>(queryKey, (menu) =>
    menu ? { ...menu, lists: menu.lists.map((list) => (list.id === id && list.newCount !== null ? { ...list, newCount: 0 } : list)) } : menu,
  );
  if (!reading) return;
  // Anche a sottomenu chiuso (`refetchType: "all"`): la lettura in corso si annulla e riparte dopo l'apertura. Alla prima lettura
  // (cache vuota) TanStack Query non la annulla e unisce la rilettura a lei: allora si rilegge appena lei finisce (rilievo di
  // Codex sulla #75, turno di notte del 10 ottobre 2026).
  if (queryClient.getQueryData(queryKey) !== undefined) {
    void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "all" });
    return;
  }
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  if (!query) return;
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.query !== query) return;
    if (event.type !== "removed" && query.state.fetchStatus === "fetching") return;
    unsubscribe();
    if (event.type !== "removed") void query.fetch().catch(() => undefined);
  });
}

/**
 * Dopo una scrittura: la lista com'è sul server in cache, il menu e lo storico da rileggere, e le righe dei Lead, che con la colonna
 * «In Smart List» (LEAD-13) portano nomi, appartenenze e visibilità delle liste.
 */
function useAfterWrite(tab: TabContext) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return (list: SmartListDetail) => {
    queryClient.setQueryData(smartListKeys.one(tabPart, list.id), list);
    void queryClient.invalidateQueries({ queryKey: menuPrefix(tab) });
    void queryClient.invalidateQueries({ queryKey: smartListKeys.revisions(tabPart, list.id), exact: true });
    void queryClient.invalidateQueries({ queryKey: leadKeys.lists(tabPart) });
  };
}

/** «Crea Smart List» e «Salva come nuova» (`POST /api/smart-lists`): la risposta è la lista nuova. */
export function useCreateSmartList(tab: TabContext) {
  const afterWrite = useAfterWrite(tab);
  return useMutation({
    mutationFn: async (body: CreateSmartListBody) => (await apiRequest("POST", "/api/smart-lists", { schema: smartListWriteResponseSchema, body, tab })).list,
    onSuccess: afterWrite,
  });
}

/** «Salva» (`PUT /api/smart-lists/:id`): filtri e ordinamento dalla revisione letta (409 se non è l'ultima). */
export function useSaveSmartList(tab: TabContext, id: string) {
  const afterWrite = useAfterWrite(tab);
  return useMutation({
    mutationFn: async (body: SaveSmartListBody) => (await apiRequest("PUT", listPath(id), { schema: smartListWriteResponseSchema, body, tab })).list,
    onSuccess: afterWrite,
  });
}

/** «Condividi» (`POST /api/smart-lists/:id/share`): chi la vede e, con la modifica di tutte le liste, l'owner. */
export function useShareSmartList(tab: TabContext, id: string) {
  const afterWrite = useAfterWrite(tab);
  return useMutation({
    mutationFn: async (body: ShareSmartListBody) => (await apiRequest("POST", `${listPath(id)}/share`, { schema: smartListWriteResponseSchema, body, tab })).list,
    onSuccess: afterWrite,
  });
}

/** «Rinomina» (`POST /api/smart-lists/:id/rename`). */
export function useRenameSmartList(tab: TabContext, id: string) {
  const afterWrite = useAfterWrite(tab);
  return useMutation({
    mutationFn: async (body: RenameSmartListBody) => (await apiRequest("POST", `${listPath(id)}/rename`, { schema: smartListWriteResponseSchema, body, tab })).list,
    onSuccess: afterWrite,
  });
}

/**
 * «Elimina la lista» (`POST /api/smart-lists/:id/archive`, 204): la lista sparisce subito dal menu in cache (la voce
 * «Smart List» non la riapre più, nemmeno prima che il menu si rilegga). La lista stessa e il suo storico escono dalla
 * cache con `forgetSmartList`, quando la pagina della lista non c'è più: altrimenti la pagina la rileggerebbe.
 */
export function useArchiveSmartList(tab: TabContext, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest("POST", `${listPath(id)}/archive`, { schema: z.undefined(), tab }),
    onSuccess: () => {
      queryClient.setQueriesData<SmartListsResponse>({ queryKey: menuPrefix(tab) }, (menu) =>
        menu ? { ...menu, lists: menu.lists.filter((list) => list.id !== id) } : menu,
      );
      void queryClient.invalidateQueries({ queryKey: menuPrefix(tab) });
      // Una lista archiviata non compare più in «In Smart List».
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists(tabKey(tab)) });
    },
  });
}

/** Toglie dalla cache una lista eliminata e il suo storico: chi la riapre trova la lista che non c'è più. */
export function forgetSmartList(queryClient: QueryClient, tab: TabContext, id: string): void {
  queryClient.removeQueries({ queryKey: smartListKeys.one(tabKey(tab), id) });
}

// ---------------------------------------------------------------------------------------------------------------------
// Il Power Dialer dalla lista (piano M5, Task 6): la sessione interrotta da riprendere, i Lead che ha già fatto, e
// l'interruzione di una sessione viva su un'altra sorgente per chiamare da qui. Le letture partono soltanto per chi chiama
// da questa scheda (`tab` non nullo): mai nella «Guarda come…», nel contesto di piattaforma o senza `call` (ruling Q27).

/**
 * `GET /api/dialer-sessions/saved?source=smart_list:<id>`: la sessione interrotta di chi guarda su questa lista, per
 * «Riprendi a chiamare» (quando, quanti Lead fatti, da chi si riparte), `null` senza. Con `tab` nullo niente.
 */
export function savedListSessionQuery(tab: TabContext | null, smartListId: string) {
  return savedDialerSessionQuery(
    tab,
    { type: "smart_list", smartListId },
    tab ? smartListKeys.savedSession(tabKey(tab), smartListId) : (["smart-list", smartListId, "dialer-saved", "spenta"] as const),
  );
}

/** Il segno di un Lead già fatto nella sessione da riprendere: chiamato, oppure saltato o passato oltre senza chiamarlo. */
export type SavedWorkMark = "called" | "skipped";

/**
 * I Lead già fatti nella sessione interrotta (`worked` di `GET /api/dialer-sessions/:id/queue`, i più recenti cento che chi
 * guarda vede ancora), per id del Lead: «Chiamato» per chi è stato chiamato, «Saltato» per chi è stato saltato con «Salta» o
 * passato al successivo senza chiamarlo (come il mock-up, che li mette insieme). Senza sessione niente. Una sessione interrotta
 * non cambia più: si legge una volta per interruzione (ripresa e interrotta di nuovo, `interruptedAt` è un altro).
 */
export function savedSessionMarksQuery(tab: TabContext | null, smartListId: string, saved: { id: string; interruptedAt: string } | null) {
  const sessionId = saved?.id ?? null;
  return queryOptions({
    queryKey:
      tab && saved ? smartListKeys.savedMarks(tabKey(tab), smartListId, saved.id, saved.interruptedAt) : (["smart-list", smartListId, "dialer-saved", "segni", "spenti"] as const),
    queryFn:
      tab && sessionId
        ? async ({ signal }) => {
            const queue = await apiRequest("GET", `/api/dialer-sessions/${encodeURIComponent(sessionId)}/queue`, { schema: dialerQueueSchema, tab, signal });
            const marks: Record<string, SavedWorkMark> = Object.fromEntries(
              queue.worked.map((item): [string, SavedWorkMark] => [item.lead.id, item.result === "called" ? "called" : "skipped"]),
            );
            return marks;
          }
        : skipToken,
    staleTime: Infinity,
  });
}

const nameCollator = new Intl.Collator("it", { sensitivity: "base" });
const byName = (left: SmartListSummary, right: SmartListSummary) =>
  nameCollator.compare(normalizeSearchText(left.name), normalizeSearchText(right.name)) || nameCollator.compare(left.name, right.name);

/**
 * Il menu dopo un nuovo ordine di un gruppo (SL-11), come lo darà il server: lo usa la scrittura ottimistica di
 * `useSaveSmartListOrder`. Con `pinned` le liste indicate sono le fissate, in quell'ordine, e le altre tornano nel loro
 * gruppo con il posto che avevano; con un altro gruppo le liste indicate prendono il posto del loro indice. L'ordine è quello
 * del server: le fissate, poi i gruppi di `SMART_LIST_GROUPS`, nel gruppo le liste spostate per posto e dopo le mai spostate
 * nell'ordine che il server ha già dato (per nome). Soltanto una lista appena tolta dalle fissate e mai spostata entra fra
 * queste per nome, con il confronto dell'italiano: il server usa il nome normalizzato del database, e in un caso limite la
 * rilettura dopo può metterla un posto più in là.
 */
export function applySmartListOrder(lists: readonly SmartListSummary[], body: SaveSmartListOrderBody): SmartListSummary[] {
  const index = new Map(body.ids.map((id, at) => [id, at]));
  const unpinned = new Set<string>();
  const next = lists.map((list): SmartListSummary => {
    if (body.group === "pinned") {
      const pinned = index.has(list.id);
      if (list.pinned && !pinned) unpinned.add(list.id);
      return pinned === list.pinned ? list : { ...list, pinned };
    }
    const at = index.get(list.id);
    return at === undefined || at === list.position ? list : { ...list, position: at };
  });
  const pinnedLists = next.filter((list) => list.pinned);
  if (body.group === "pinned") pinnedLists.sort((left, right) => (index.get(left.id) ?? 0) - (index.get(right.id) ?? 0));
  const grouped = SMART_LIST_GROUPS.flatMap((group) => {
    const inGroup = next.filter((list) => !list.pinned && list.group === group);
    // `sort` è stabile: a parità di posto resta l'ordine che c'era.
    const placed = inGroup.filter((list) => list.position !== null).sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
    const never: SmartListSummary[] = [];
    for (const list of inGroup.filter((item) => item.position === null)) {
      if (!unpinned.has(list.id)) {
        never.push(list);
        continue;
      }
      const at = never.findIndex((other) => !unpinned.has(other.id) && byName(list, other) < 0);
      never.splice(at === -1 ? never.length : at, 0, list);
    }
    return [...placed, ...never];
  });
  return [...pinnedLists, ...grouped];
}

/** Una mossa partita prima che un'altra della stessa fila fallisse: non si manda (il menu è già tornato com'era e si rilegge). */
class StaleOrderMove extends Error {
  constructor() {
    super("Smart List order move superseded by an earlier failure");
    this.name = "StaleOrderMove";
  }
}

/** Per ogni fila (una per azienda della scheda), quante mosse sono state rifiutate finora. */
const orderFailures = new Map<string, number>();

type OrderMove = { body: SaveSmartListOrderBody; failures: number };

/**
 * «Fissa in alto», «Togli dalle fissate», «Sposta su», «Sposta giù» e il trascinamento (`PUT /api/smart-lists/order`, SL-11):
 * il menu in cache cambia subito, in ogni vista della scheda (l'ordine è di chi guarda anche nella Vista Commerciale), e la
 * richiesta parte dopo. Le richieste vanno in fila (`scope`): l'ultima, che porta l'ordine intero del gruppo, vince. Se il
 * server ne rifiuta una, il menu torna com'era prima di lei, un avviso lo dice (`onFailed`) e il menu si rilegge; le mosse
 * fatte dopo di lei e ancora in fila non partono più, perché il loro ordine conteneva quella rifiutata. Una lettura del menu
 * in corso si annulla prima della mossa, perché porterebbe l'ordine di prima; i conteggi non si rifanno. Dopo il successo
 * la mossa si riapplica al menu in cache, se nessun'altra è in fila: una lettura partita mentre era in viaggio (dopo una
 * scrittura sui Lead, `menu-refresh.ts`) poteva riportare l'ordine di prima.
 */
export function useSaveSmartListOrder(tab: TabContext, onFailed: () => void): { save(body: SaveSmartListOrderBody): void } {
  const queryClient = useQueryClient();
  const prefix = menuPrefix(tab);
  const queue = `smart-list-order:${tabKey(tab)}`;
  const mutationKey = ["smart-list-order", tabKey(tab)];
  const { mutate } = useMutation({
    mutationKey,
    scope: { id: queue },
    mutationFn: ({ body, failures }: OrderMove) => {
      if ((orderFailures.get(queue) ?? 0) !== failures) return Promise.reject(new StaleOrderMove());
      return apiRequest("PUT", "/api/smart-lists/order", { schema: z.undefined(), body, tab });
    },
    onMutate: async ({ body }) => {
      await queryClient.cancelQueries({ queryKey: prefix });
      const before = queryClient.getQueriesData<SmartListsResponse>({ queryKey: prefix });
      queryClient.setQueriesData<SmartListsResponse>({ queryKey: prefix }, (menu) => (menu ? { ...menu, lists: applySmartListOrder(menu.lists, body) } : menu));
      return { before };
    },
    onError: (error, _move, context) => {
      if (error instanceof StaleOrderMove) return;
      orderFailures.set(queue, (orderFailures.get(queue) ?? 0) + 1);
      for (const [queryKey, menu] of context?.before ?? []) queryClient.setQueryData(queryKey, menu);
      onFailed();
      void queryClient.invalidateQueries({ queryKey: prefix });
    },
    onSuccess: (_data, { body }) => {
      // Questa mossa conta ancora fra quelle in corso.
      if (queryClient.isMutating({ mutationKey }) <= 1) {
        queryClient.setQueriesData<SmartListsResponse>({ queryKey: prefix }, (menu) => (menu ? { ...menu, lists: applySmartListOrder(menu.lists, body) } : menu));
      }
      if (queryClient.isFetching({ queryKey: prefix }) > 0) void queryClient.invalidateQueries({ queryKey: prefix });
    },
  });
  const save = useCallback((body: SaveSmartListOrderBody) => mutate({ body, failures: orderFailures.get(queue) ?? 0 }), [mutate, queue]);
  return { save };
}
